import { inject, Injectable } from '@angular/core';
import { State, Action, Selector, StateContext, Store } from '@ngxs/store';
import { SubjectActions } from './subject.actions';
import { SubjectService } from '../../services/subject.service';
import { catchError, filter, of, switchMap, tap } from 'rxjs';
import { SubjectJsonModel } from '../../subject.json.model';
import { MatDialog } from '@angular/material/dialog';
import { EditSubjectComponent } from '../../edit-subject/edit-subject.component';
import { ConfirmDialogComponent, DeleteDialogData } from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { FetchOptionsStateModel } from '../../../core/state/fetchOptions.state';

export interface SubjectStateModel {
  subjects: SubjectJsonModel[]
}

@State<SubjectStateModel>({
  name: 'subject',
  defaults: {
    subjects: []
  }
})
@Injectable()
export class SubjectState {
  private readonly subjectService = inject(SubjectService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  @Selector()
  static getState(state: SubjectStateModel) {
    return state;
  }

  @Selector()
  static getSubjects(state: SubjectStateModel) {
    return state.subjects;
  }

  @Selector()
  static getAmountOfSubjects(state: SubjectStateModel) {
    return state.subjects?.length ?? 0;
  }

  @Selector()
  static subjectsIsEmpty(state: SubjectStateModel) {
    return (state.subjects?.length ?? 0) <= 0;
  }

  @Action(SubjectActions.Fetch)
  onFetchSubjects(ctx: StateContext<SubjectStateModel>) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);
    return this.subjectService.getAllWithPagination({
      queryParams: {
        filter: {
          root: 'true'
        }
      }
    }, localFetchOptions).pipe(
      tap(res => ctx.dispatch(new SubjectActions.FetchSuccess(res[0])))
    );
  }

  @Action(SubjectActions.FetchAll)
  onFetchAllSubjects(ctx: StateContext<SubjectStateModel>) {
    return this.subjectService.getMany({
      queryParams: {
        sort: 'name'
      }
    }).pipe(
      tap(res => ctx.dispatch(new SubjectActions.FetchSuccess(res.data)))
    );
  }

  @Action(SubjectActions.FetchSuccess)
  onFetchSubjectsSuccess(ctx: StateContext<SubjectStateModel>, { payload }: SubjectActions.FetchSuccess) {
    ctx.setState({ subjects: payload });
  }

  @Action(SubjectActions.Add)
  onAdd(ctx: StateContext<SubjectStateModel>, { payload }: SubjectActions.Add) {
    return this.dialog.open<EditSubjectComponent, SubjectJsonModel, SubjectJsonModel>(EditSubjectComponent, { data: payload })
      .afterClosed()
      .pipe(
        filter((subject): subject is SubjectJsonModel => !!subject),
        switchMap(subject => this.subjectService.createAndSave(subject)),
        // currently ignores errors
        catchError(() => of()),
        tap(() => ctx.dispatch(new SubjectActions.Fetch()))
      );
  }

  @Action(SubjectActions.Edit)
  onEdit(ctx: StateContext<SubjectStateModel>, { payload }: SubjectActions.Edit) {
    return this.dialog.open<EditSubjectComponent, SubjectJsonModel, SubjectJsonModel>(EditSubjectComponent, { data: payload })
      .afterClosed()
      .pipe(
        filter((subject): subject is SubjectJsonModel => !!subject),
        switchMap(subject => this.subjectService.update(subject)),
        // currently ignores errors
        catchError(() => of()),
        tap(() => ctx.dispatch(new SubjectActions.Fetch()))
      );
  }

  @Action(SubjectActions.Delete)
  onDelete(ctx: StateContext<SubjectJsonModel>, { payload }: SubjectActions.Delete) {
    return this.dialog.open<ConfirmDialogComponent, DeleteDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        id: payload.id,
        name: payload.name,
        itemType: 'Fach/Beruf',
        deleteFn: () => {
          return payload.destroy();
        }
      }
    })
      .afterClosed()
      .pipe(
        tap(success => {
          if (!success) return;
          ctx.dispatch(new SubjectActions.Fetch());
        })
      );
  }
}
